import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Notice } from '@rydres/ui';
import { enablePush, isAndroid, isIos, isStandalone, pushState, type PushState } from '../lib/push.js';

type BeforeInstallPromptEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> };
let deferredPrompt: BeforeInstallPromptEvent | null = null;
if (typeof window !== 'undefined')
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
  });

const DISMISS_KEY = 'rydres.installcard.dismissed';

/**
 * Walks a driver through putting RydRes on the phone and turning notifications on.
 * Shown on Today until both are done or the driver dismisses it.
 */
export function InstallCard() {
  const [installed, setInstalled] = useState(isStandalone());
  const [push, setPush] = useState<PushState | null>(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    void pushState().then(setPush);
    const onInstalled = () => setInstalled(true);
    window.addEventListener('appinstalled', onInstalled);
    return () => window.removeEventListener('appinstalled', onInstalled);
  }, []);
  if (dismissed || push === null || (installed && push === 'on')) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* fine */
    }
    setDismissed(true);
  };
  const turnOn = async () => {
    setBusy(true);
    try {
      setPush(await enablePush());
    } catch {
      setPush('off');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title={installed ? 'Turn on notifications' : 'Put RydRes on your phone'}
      info={
        installed ? (
          <>Get a buzz the moment a rider books or asks for a price, even with the app closed. You still get the text as well. You can change this later under <Link className="underline" to="/settings/notifications">Settings → Notifications</Link>.</>
        ) : (
          'Install it like an app: it opens full screen from your home screen, and it is the only way to get notifications on iPhone.'
        )
      }
      right={<button onClick={dismiss} className="text-xs text-muted">Hide</button>}
    >
      {!installed && (
        <div className="space-y-2 text-sm">
          {isIos() ? (
            <ol className="list-decimal space-y-1 pl-5 text-ink2">
              <li>Open this page in <strong>Safari</strong>.</li>
              <li>Tap the <strong>Share</strong> button (the square with an arrow).</li>
              <li>Scroll and tap <strong>Add to Home Screen</strong>, then <strong>Add</strong>.</li>
              <li>Open RydRes from the home screen and come back here.</li>
            </ol>
          ) : deferredPrompt ? (
            <Button
              small
              onClick={async () => {
                await deferredPrompt!.prompt();
                const { outcome } = await deferredPrompt!.userChoice;
                if (outcome === 'accepted') setInstalled(true);
                deferredPrompt = null;
              }}
            >
              Install RydRes
            </Button>
          ) : isAndroid() ? (
            <p className="text-ink2">In Chrome, open the <strong>⋮</strong> menu and tap <strong>Install app</strong> or <strong>Add to Home screen</strong>.</p>
          ) : (
            <p className="text-ink2">On your phone, open <strong>app.rydres.com</strong> and use the browser's "Add to Home Screen" option.</p>
          )}
        </div>
      )}
      {installed && push !== 'on' && (
        <div className="space-y-2 text-sm">
          {push === 'denied' ? (
            <Notice tone="warn">Notifications are blocked for RydRes in your phone's settings. Allow them there, then come back.</Notice>
          ) : push === 'unsupported' || push === 'needs-install' ? (
            <Notice tone="info">This browser cannot show notifications. Texts still arrive for every booking.</Notice>
          ) : (
            <Button small disabled={busy} onClick={() => void turnOn()}>{busy ? 'One moment…' : 'Turn on notifications'}</Button>
          )}
        </div>
      )}
    </Card>
  );
}
